import { Button, type ButtonProps } from "./Button";
import { Icon } from "./Icon";
import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { useRovingFocus, useRovingTabStop } from "@/hooks/use-roving-focus";
import { createContext, forwardRef, useContext, type ReactNode } from "react";

type ChipGroupContextValue = { value: string | null; onValueChange: (value: string) => void };

const ChipGroupContext = createContext<ChipGroupContextValue | null>(null);

const CHIP_SIZE = { sm: "h-8 px-3 text-sm", md: "h-10 px-4 text-base" } as const;

export type ChipProps = Omit<ButtonProps, "variant" | "tone" | "size" | "shape" | "selected" | "value"> & {
  /** Option value inside a single-select ChipGroup (role radio); the group decides `selected`. */
  value?: string;
  selected?: boolean;
  size?: "sm" | "md";
  /** Unselected look: `surface` (card + shadow) or `soft` (neutral tint). */
  appearance?: "surface" | "soft";
  icon?: IconGlyph;
  children: ReactNode;
};

/** Pill toggle: solid primary when selected; a radio inside ChipGroup, otherwise `aria-pressed`. */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { value, selected, size = "md", appearance = "surface", icon, children, className, onClick, ...rest },
  ref,
) {
  const group = useContext(ChipGroupContext);
  const inGroup = group !== null && value !== undefined;
  const isSelected = inGroup ? group.value === value : Boolean(selected);
  const semantics = inGroup
    ? { role: "radio", "aria-checked": isSelected }
    : { "aria-pressed": isSelected };

  return (
    <Button
      {...rest}
      {...semantics}
      ref={ref}
      data-value={value}
      variant={isSelected ? "solid" : appearance}
      tone={isSelected ? "primary" : "neutral"}
      size="none"
      shape="pill"
      selected={isSelected}
      onClick={(event) => {
        onClick?.(event);
        if (inGroup && !event.defaultPrevented) group.onValueChange(value);
      }}
      className={cn(
        "shrink-0",
        CHIP_SIZE[size],
        isSelected || appearance === "soft" ? "font-semibold" : "font-normal",
        !isSelected && appearance === "surface" && "border-0 shadow-card",
        className,
      )}
    >
      {icon ? <Icon as={icon} size="sm" /> : null}
      {children}
    </Button>
  );
});

export type ChipGroupProps = {
  /** Accessible name of the row. */
  label: string;
  /** Single selection; with it the row is a `radiogroup` of `Chip value=` children. */
  value?: string | null;
  onValueChange?: (value: string) => void;
  /** `scroll` keeps one row that scrolls sideways; `wrap` breaks into lines. */
  layout?: "scroll" | "wrap";
  className?: string;
  children: ReactNode;
};

/** Row of chips: a radiogroup with roving focus (arrows, Home/End) when `onValueChange` is given, else a `group`. */
export const ChipGroup = forwardRef<HTMLDivElement, ChipGroupProps>(function ChipGroup(
  { label, value = null, onValueChange, layout = "scroll", className, children },
  ref,
) {
  const single = onValueChange !== undefined;
  const handleKeyDown = useRovingFocus<HTMLButtonElement>({
    selector: '[role="radio"]:not(:disabled)',
    onMove: (chip) => {
      if (onValueChange && chip.dataset.value !== undefined && chip.dataset.value !== value) {
        onValueChange(chip.dataset.value);
      }
    },
  });
  const groupRef = useRovingTabStop(ref);

  const row = (
    <div
      ref={groupRef}
      role={single ? "radiogroup" : "group"}
      aria-label={label}
      onKeyDown={single ? handleKeyDown : undefined}
      className={cn("flex gap-2", layout === "scroll" ? "overflow-x-auto pb-1" : "flex-wrap", className)}
    >
      {children}
    </div>
  );

  return single ? (
    <ChipGroupContext.Provider value={{ value, onValueChange }}>{row}</ChipGroupContext.Provider>
  ) : (
    row
  );
});
